import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { axiosInstance } from "../App";
import { Repeat, Calendar as CalendarIcon, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

function TaskRecurrence({ taskId, isRecurring, recurrencePattern, onUpdate }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pattern, setPattern] = useState({
    type: "daily",
    interval: 1,
    days: [],
    day_of_month: 1,
    end_date: null
  });
  const [saving, setSaving] = useState(false);
  const [instances, setInstances] = useState([]);
  const [showInstances, setShowInstances] = useState(false);

  useEffect(() => {
    if (recurrencePattern) {
      setPattern({
        type: recurrencePattern.type || "daily",
        interval: recurrencePattern.interval || 1,
        days: recurrencePattern.days || [],
        day_of_month: recurrencePattern.day_of_month || 1,
        end_date: recurrencePattern.end_date || null
      });
    }
  }, [recurrencePattern]);

  useEffect(() => {
    if (isRecurring) {
      fetchInstances();
    }
  }, [isRecurring, taskId]);

  const fetchInstances = async () => {
    try {
      const response = await axiosInstance.get(`/tasks/${taskId}/instances`);
      setInstances(response.data);
    } catch (error) {
      console.error("Failed to fetch instances", error);
    }
  };

  const handleSetRecurrence = async () => {
    setSaving(true);
    try {
      await axiosInstance.post(`/tasks/${taskId}/recurrence`, {
        recurrence_pattern: pattern
      });
      toast.success("Tekrarlama ayarlandı");
      setDialogOpen(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Tekrarlama ayarlanamadı");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRecurrence = async () => {
    try {
      await axiosInstance.delete(`/tasks/${taskId}/recurrence`);
      toast.success("Tekrarlama kaldırıldı");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Tekrarlama kaldırılamadı");
    }
  };

  const handleCreateInstance = async () => {
    try {
      await axiosInstance.post(`/tasks/${taskId}/create-instance`);
      toast.success("Yeni görev oluşturuldu");
      fetchInstances();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Görev oluşturulamadı");
    }
  };

  const toggleDay = (day) => {
    setPattern(prev => {
      const days = prev.days.includes(day)
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day];
      return { ...prev, days };
    });
  };

  const weekDays = [
    { value: 0, label: "Pzt" },
    { value: 1, label: "Sal" },
    { value: 2, label: "Çar" },
    { value: 3, label: "Per" },
    { value: 4, label: "Cum" },
    { value: 5, label: "Cmt" },
    { value: 6, label: "Paz" }
  ];

  const getRecurrenceText = () => {
    if (!recurrencePattern) return "";
    
    const { type, interval, days, day_of_month } = recurrencePattern;
    
    if (type === "daily") {
      return interval === 1 ? "Her gün" : `Her ${interval} günde bir`;
    } else if (type === "weekly") {
      if (days && days.length > 0) {
        const dayNames = days.map(d => weekDays[d].label).join(", ");
        return `Her hafta: ${dayNames}`;
      }
      return interval === 1 ? "Her hafta" : `Her ${interval} haftada bir`;
    } else if (type === "monthly") {
      return `Her ayın ${day_of_month}. günü`;
    }
    return "Tekrarlayan";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Repeat className="h-4 w-4" />
          <h4 className="font-semibold">Tekrarlama</h4>
        </div>
        {isRecurring && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowInstances(!showInstances)}
          >
            {showInstances ? "Gizle" : `Görevler (${instances.length})`}
          </Button>
        )}
      </div>

      {isRecurring ? (
        <div className="space-y-3">
          <div className="p-3 border rounded-lg bg-green-50 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">{getRecurrenceText()}</p>
                {recurrencePattern?.end_date && (
                  <p className="text-xs text-green-600 mt-1">
                    Bitiş: {format(new Date(recurrencePattern.end_date), "dd MMM yyyy", { locale: tr })}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCreateInstance}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Yeni Oluştur
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleRemoveRecurrence}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
          </div>

          {showInstances && instances.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Oluşturulan Görevler:</p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {instances.map((instance) => (
                  <div
                    key={instance.id}
                    className="p-2 border rounded text-sm flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium">{instance.title}</p>
                      <p className="text-xs text-gray-500">
                        {instance.due_date && format(new Date(instance.due_date), "dd MMM yyyy", { locale: tr })}
                      </p>
                    </div>
                    <Badge variant={instance.status === "done" ? "success" : "secondary"}>
                      {instance.status === "done" ? "Tamamlandı" : instance.status === "in-progress" ? "Devam Ediyor" : "Yapılacak"}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full">
              <Repeat className="h-4 w-4 mr-2" />
              Tekrarlama Ayarla
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Tekrarlama Ayarları</DialogTitle>
              <DialogDescription>
                Bu görevin otomatik olarak tekrarlanması için ayarlar yapın
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Pattern Type */}
              <div className="space-y-2">
                <Label>Tekrarlama Türü</Label>
                <Select value={pattern.type} onValueChange={(value) => setPattern({ ...pattern, type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Günlük</SelectItem>
                    <SelectItem value="weekly">Haftalık</SelectItem>
                    <SelectItem value="monthly">Aylık</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Interval */}
              <div className="space-y-2">
                <Label>Aralık</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    value={pattern.interval}
                    onChange={(e) => setPattern({ ...pattern, interval: parseInt(e.target.value) || 1 })}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-600">
                    {pattern.type === "daily" && "gün"}
                    {pattern.type === "weekly" && "hafta"}
                    {pattern.type === "monthly" && "ay"}
                  </span>
                </div>
              </div>

              {/* Weekly Days */}
              {pattern.type === "weekly" && (
                <div className="space-y-2">
                  <Label>Günler</Label>
                  <div className="flex gap-2 flex-wrap">
                    {weekDays.map((day) => (
                      <div key={day.value} className="flex items-center gap-1">
                        <Checkbox
                          id={`day-${day.value}`}
                          checked={pattern.days.includes(day.value)}
                          onCheckedChange={() => toggleDay(day.value)}
                        />
                        <label
                          htmlFor={`day-${day.value}`}
                          className="text-sm cursor-pointer"
                        >
                          {day.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly Day */}
              {pattern.type === "monthly" && (
                <div className="space-y-2">
                  <Label>Ayın Günü</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={pattern.day_of_month}
                    onChange={(e) => setPattern({ ...pattern, day_of_month: parseInt(e.target.value) || 1 })}
                    className="w-24"
                  />
                </div>
              )}

              {/* End Date */}
              <div className="space-y-2">
                <Label>Bitiş Tarihi (Opsiyonel)</Label>
                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {pattern.end_date ? format(new Date(pattern.end_date), "dd MMM yyyy", { locale: tr }) : "Seçiniz"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-auto p-0" 
                    style={{ zIndex: 999999 }}
                    align="start"
                    sideOffset={5}
                  >
                    <Calendar
                      mode="single"
                      selected={pattern.end_date ? new Date(pattern.end_date) : undefined}
                      onSelect={(date) => setPattern({ 
                        ...pattern, 
                        end_date: date ? date.toISOString().split('T')[0] : null 
                      })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {pattern.end_date && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPattern({ ...pattern, end_date: null })}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Temizle
                  </Button>
                )}
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSetRecurrence}
                disabled={saving}
                className="w-full"
              >
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default TaskRecurrence;
